import { useEffect, useState } from "react";
import { PlatformApiClient, PlatformApiError } from "../api";
import { ActionBar, FormLayout, Section, SectionHeader, StatusState } from "../adaptive/primitives";
import type { MenuSettings } from "./types";

export function MenuSettingsView({ slug, api, onExpired }: { slug: string; api: PlatformApiClient; onExpired: () => void }) {
  const [data, setData] = useState<MenuSettings | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [freshUrl, setFreshUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  function handleFailure(cause: unknown, message: string): boolean {
    if (cause instanceof PlatformApiError && cause.status === 401) { onExpired(); return true; }
    setNotice(message); return false;
  }

  const load = () => api.menu<MenuSettings>(slug, "/settings").then((value) => { setData(value); setLoadError(null); }).catch((cause) => {
    if (cause instanceof PlatformApiError && cause.status === 401) { onExpired(); return; }
    setLoadError(cause instanceof PlatformApiError && cause.status === 403 ? "No tenés permisos para configurar esta carta." : "No pudimos cargar la configuración de Menu.");
  });
  useEffect(() => { void load(); }, [slug]);

  async function post<T>(path: string, body: unknown): Promise<T | null> {
    try { return await api.menu<T>(slug, path, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) }); }
    catch (cause) { handleFailure(cause, "No pudimos completar esa acción."); return null; }
  }

  if (loadError) return <StatusState className="menu-state" tone="danger" live="assertive" title="No pudimos cargar la configuración" message={loadError} />;
  if (!data) return <StatusState className="menu-state" tone="loading" title="Cargando configuración…" message="Estamos preparando los datos públicos y accesos de tu carta." />;

  const profileRevision = data.profile.revision;

  async function save(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault(); setBusy(true); const form = new FormData(event.currentTarget);
    try {
      const result = await post<{ message?: string }>("/settings/profile", { patch: { displayName: String(form.get("displayName")), description: String(form.get("description")), address: String(form.get("address")), phone: String(form.get("phone")) }, expectedRevision: profileRevision });
      if (!result) return; setNotice(result.message ?? "Perfil actualizado."); await load();
    } finally { setBusy(false); }
  }
  async function create(kind: "qr" | "nfc" | "link") { const result = await post<{ publicUrl: string }>("/settings/access", { kind }); if (!result) return; setFreshUrl(result.publicUrl); setNotice("Acceso creado. Guardá este enlace ahora: por seguridad no se vuelve a mostrar igual."); await load(); }
  async function rotate(access: MenuSettings["accesses"][number]) { const result = await post<{ publicUrl: string }>(`/settings/access/${encodeURIComponent(access.id)}/rotate`, { expectedRevision: access.revision }); if (!result) return; setFreshUrl(result.publicUrl); setNotice("Acceso rotado. Guardá el nuevo enlace ahora."); await load(); }
  async function revoke(access: MenuSettings["accesses"][number]) { const result = await post<{ message: string }>(`/settings/access/${encodeURIComponent(access.id)}/revoke`, { expectedRevision: access.revision }); if (!result) return; setNotice(result.message); await load(); }

  return <div className="menu-stack">
    {notice && <div className="menu-notice" role="status">{notice}</div>}
    {freshUrl && <div className="menu-access-secret"><strong>Nuevo acceso</strong><code>{freshUrl}</code></div>}

    <form className="menu-settings-form" onSubmit={save}>
      <SectionHeader className="menu-section-heading" title="Perfil público" description="Estos datos aparecen en tu carta pública." actions={<a href={data.publicUrl} target="_blank" rel="noreferrer">Ver carta pública</a>} />
      <FormLayout>
        <label>Nombre visible<input name="displayName" defaultValue={data.profile.displayName} required /></label>
        <label>Descripción<textarea name="description" rows={3} defaultValue={data.profile.description} /></label>
        <label>Dirección<input name="address" defaultValue={data.profile.address} /></label>
        <label>WhatsApp / Teléfono<input name="phone" defaultValue={data.profile.phone} /></label>
      </FormLayout>
      <ActionBar sticky="compact"><button className="primary-button" disabled={busy} type="submit">{busy ? "Guardando…" : "Guardar cambios"}</button></ActionBar>
    </form>

    <Section className="menu-category">
      <SectionHeader className="menu-section-heading" title="Accesos públicos" description="QR, NFC y enlaces administrados por Menu." />
      <ActionBar className="menu-row-actions" sticky="never" align="start">
        <button onClick={() => void create("qr")}>Crear QR</button>
        <button onClick={() => void create("nfc")}>Crear NFC</button>
        <button onClick={() => void create("link")}>Crear enlace</button>
      </ActionBar>
      {data.accesses.map((access) => <article className="menu-item-row" key={access.id}>
        <div className="menu-item-main"><strong>{access.kind.toUpperCase()}</strong><span>{access.status}</span></div>
        <ActionBar className="menu-row-actions" sticky="never"><button onClick={() => void rotate(access)}>Rotar</button><button className="menu-danger-link" onClick={() => void revoke(access)}>Revocar</button></ActionBar>
      </article>)}
    </Section>

    {data.memberships.length > 0 && <Section className="menu-category">
      <SectionHeader className="menu-section-heading" title="Personas con acceso" />
      {data.memberships.map((member) => <div className="menu-member" key={`${member.displayName}-${member.status}`}><span>{member.displayName}</span><small>{member.authorityCount} permisos · {member.status}</small></div>)}
    </Section>}
  </div>;
}
